//get all employees

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from 'react-toastify';

import {
  getGiftRequests,
  IGiftRequest,
  GiftRequestFormData,
  createGiftRequest,
  updateGiftRequest,
  deleteGiftRequest,
  GiftRequestStatus,
  approveGiftRequest,
  generateCertificate,
  ICertificateParams,
  updateCertificateStatus,
  ICertificateIdsAndStatuses,
  getCertificatesRequests,
  sendToManagerMarketing,
  getEmployeeMonthlyStatistics,
  CustomerMonthlyStatistics,
  getGiftRequestTotalStatistics,
  GiftRequestTotalStatistics,
} from '@/api/gifts-requests';
import { apiClient } from '@/api/axios';
import { GiftDTO, parseGiftDTO } from './useGifts';
import { EmployeeDTO, parseEmployeeDTO } from './useEmployee';
import { ICustomer } from '@/api/customers';
import { parseCustomerDTO } from './useCustomer';

// DTO cho gift request
export interface GiftRequestDTO {
  id: string;
  gift: GiftDTO;
  customers: ICustomer[];
  reason: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  status: GiftRequestStatus;
  createdBy: EmployeeDTO;
  assignedTo: EmployeeDTO;
  numberOfGiftsPerCustomer: number;
  reasonReject: string;
  explanation: string;
  usedRejectBy: EmployeeDTO | null;
  timeReject: string | null;
}

// Chuyển đổi từ IGiftRequestResponse sang GiftRequestDTO
export const parseGiftRequestDTO = (
  giftRequest: IGiftRequest
): GiftRequestDTO => ({
  id: giftRequest.id,
  gift: parseGiftDTO(giftRequest.gift),
  customers: giftRequest.customers.map((customer) =>
    parseCustomerDTO(customer)
  ),
  reason: giftRequest.reason,
  createdAt: giftRequest.createdAt,
  updatedAt: giftRequest.updatedAt,
  deletedAt: giftRequest.deletedAt,
  status: giftRequest.status,
  createdBy: parseEmployeeDTO(giftRequest.createdBy),
  assignedTo: parseEmployeeDTO(giftRequest.assignedTo),
  numberOfGiftsPerCustomer: giftRequest.numberOfGiftsPerCustomer,
  reasonReject: giftRequest.reasonReject,
  explanation: giftRequest.explanation,
  usedRejectBy: giftRequest.usedRejectBy
    ? parseEmployeeDTO(giftRequest.usedRejectBy)
    : null,
  timeReject: giftRequest.timeReject,
});

// Lấy danh sách gift requests (có phân trang, tìm kiếm)
export const useGiftRequests = ({
  page,
  limit,
  employeeId,
  status,
  customerId,
  search,
}: {
  page: number;
  limit: number;
  employeeId: string | null;
  status: GiftRequestStatus | null;
  customerId: string | null;
  search: string;
}) => {
  return useQuery<{
    giftRequests: GiftRequestDTO[];
    total: number;
    pages: number;
    hasNextPage: boolean;
  }>({
    queryKey: [
      'gift-requests',
      page,
      limit,
      employeeId,
      status,
      customerId,
      search,
    ],
    queryFn: async () => {
      const response = await getGiftRequests(
        page,
        limit,
        employeeId,
        status,
        customerId,
        search
      );
      const giftRequests = response.items.map(parseGiftRequestDTO);
      return {
        giftRequests,
        total: response.totalItems,
        pages: response.totalPages,
        hasNextPage: response.currentPage < response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
  });
};
// Tạo gift request
export const useCreateGiftRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: GiftRequestFormData) => {
      const response = await createGiftRequest(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gift-requests'] });
      toast.success('Tạo yêu cầu quà thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Cập nhật gift request
export const useUpdateGiftRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: GiftRequestFormData;
    }) => {
      const response = await updateGiftRequest(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gift-requests'] });
      toast.success('Cập nhật yêu cầu quà thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Xóa gift request
export const useDeleteGiftRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteGiftRequest(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gift-requests'] });
      toast.success('Xóa yêu cầu quà thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useApproveGiftRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      reasonReject,
    }: {
      id: string;
      status: GiftRequestStatus;
      reasonReject?: string;
    }) => {
      const response = await approveGiftRequest(id, status, reasonReject);
      return response;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['gift-requests'] });
      toast.success('Duyệt yêu cầu quà thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useGenerateCertificate = () => {
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: ICertificateParams;
    }) => {
      const response = await generateCertificate(id, payload);
      return response;
    },
  });
};

export const useUpdateCertificateStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ICertificateIdsAndStatuses[]) => {
      const response = await updateCertificateStatus(payload);
      return response;
    },
    onSuccess: (data) => {
      toast.success('Cập nhật trạng thái chứng từ thành công');
      queryClient.invalidateQueries({ queryKey: ['gift-requests'] });
      queryClient.invalidateQueries({
        queryKey: [`certificates-requests-${data[0].giftRequest.id}`],
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// /api/certificates
export const useGetAcceptanceRequests = (giftRequestId: string) => {
  return useQuery({
    queryKey: [`certificates-requests-${giftRequestId}`, giftRequestId],
    queryFn: async () => {
      const response = await getCertificatesRequests(giftRequestId);
      return response;
    },
    enabled: !!giftRequestId,
  });
};

// send to manager marketing
export const useSendToManagerMarketing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await sendToManagerMarketing(id);
      return response;
    },
    onSuccess: () => {
      toast.success('Đã gửi yêu cầu đến quản lý');
      queryClient.invalidateQueries({ queryKey: ['gift-requests'] });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// New hook for employee monthly statistics
export const useEmployeeMonthlyStatistics = (
  employeeId: number | undefined,
  year: number
) => {
  return useQuery({
    queryKey: ['employee-monthly-statistics', employeeId, year],
    queryFn: () => getEmployeeMonthlyStatistics(employeeId!, year),
    enabled: !!employeeId,
  });
};

// Hook cho thống kê theo khách hàng
export const useCustomerMonthlyStatistics = (
  customerId: number | undefined,
  year: number
) => {
  return useQuery({
    queryKey: ['customer-monthly-statistics', customerId, year],
    queryFn: async () => {
      if (!customerId) return null;
      const response = await apiClient.get<CustomerMonthlyStatistics>(
        `gift-requests/statistics/customer/${customerId}/monthly/${year}`
      );
      return response.data;
    },
    enabled: !!customerId,
  });
};

export const useGiftRequestStatistics = (year: number) => {
  return useQuery<GiftRequestTotalStatistics>({
    queryKey: ['gift-request-statistics', year],
    queryFn: async () => {
      return await getGiftRequestTotalStatistics(year);
    },
    enabled: !!year,
  });
};
